import React, { useEffect, useRef } from 'react';
import { TextField, TextFieldProps } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { useGetTestRailApiSections } from '../TestRail.hooks';
import { TestRailApiSection } from '../TestRail.interface';

export interface TestRailApiSectionPickerProps {
  testRailProjectId: number | undefined;
  testRailSuiteId: number | undefined;
  section: TestRailApiSection | null | undefined;
  disabled?: boolean;
  className?: string;
  textFieldProps?: TextFieldProps;
  onChange?: (section: TestRailApiSection | null) => void;
}

const TestRailApiSectionPicker: React.FC<
  TestRailApiSectionPickerProps
> = props => {
  const {
    testRailProjectId,
    testRailSuiteId,
    section,
    disabled,
    className,
    textFieldProps,
    onChange
  } = props;
  const shouldFetchRef = useRef(true);
  const {
    sectionList,
    isLoading: isFindingSections,
    error: getSectionError,
    getSections,
    abortGetSections,
    setState: setSectionState
  } = useGetTestRailApiSections();
  const classes: string[] = [];
  className && classes.push(className);

  // when provided ids change, make sure to refetch
  useEffect(() => {
    shouldFetchRef.current = true;
  }, [testRailProjectId, testRailSuiteId]);

  // cancel ongoing requests when disabled
  useEffect(() => {
    if (!disabled) return;
    abortGetSections();
    setSectionState({ isLoading: false });
  }, [disabled, abortGetSections, setSectionState]);

  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      loading={isFindingSections}
      value={section || null}
      options={sectionList?.sections || []}
      className={classes.join(' ')}
      getOptionLabel={section => section.name}
      getOptionSelected={(option, value) => option.id === value.id}
      onOpen={async () => {
        // initial fetch on open
        if (
          !isFindingSections &&
          testRailProjectId &&
          testRailSuiteId &&
          (shouldFetchRef.current ||
            !sectionList ||
            sectionList.sections.length === 0)
        ) {
          setSectionState({ isLoading: true });
          const { data } = await getSections({
            testRailProjectId,
            testRailSuiteId
          });
          shouldFetchRef.current = !data;
        }
      }}
      onInputChange={() => {
        // clear any errors when input changes
        setSectionState(state => ({ ...state, error: undefined }));
      }}
      onChange={(_, value) => {
        // clear any errors when selection changes
        setSectionState(state => ({ ...state, error: undefined }));

        onChange?.(value);
      }}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth
          variant='outlined'
          label='Section'
          placeholder='Select TestRail Section'
          {...textFieldProps}
          error={!!getSectionError || textFieldProps?.error}
          helperText={getSectionError?.message || textFieldProps?.helperText}
        />
      )}
    />
  );
};

export default TestRailApiSectionPicker;
